import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  getActivityDetailsV1,
  getActivityExerciseGroups,
  getWeeklyScheduledServices,
  getWeeklyWorkoutOverview,
  getWorkoutAnalysis,
} from "../../../../../../common/network/workoutPlanApi";
import { LoadStatus } from "../../shared/loadStatus";
import { addDaysISO, thisWeekMonday, todayISO } from "../../shared/planDates";
import { findActivity, findEvent, findService } from "../helpers/activitySelectors";
import { isV2Detail } from "../helpers/exerciseSelectors";
import {
  Activity,
  ActivityEvent,
  ExerciseGroupV2Model,
  ExerciseV1Model,
  ScheduledService,
  WeeklyWorkoutOverviewResponse,
  WorkoutAnalysisType,
  WorkoutDay,
  WorkoutDetails,
  WorkoutWeek,
} from "../model/workoutPlanTypes";
import {
  AnalysisSelection,
  createInitialState,
  DetailsSelection,
  detailsKey,
  PlanAvailability,
  selectDay,
  workoutPlanReducer,
} from "./workoutPlanReducer";

/**
 * Fetchers are injectable so tests can run the provider without the network.
 * Production uses `workoutPlanApi`.
 */
export type WorkoutPlanFetchers = {
  getWeeklyOverview: (
    monday: string,
    role: number
  ) => Promise<WeeklyWorkoutOverviewResponse | null>;
  getScheduledServices: (
    monday: string,
    role: number
  ) => Promise<ScheduledService[]>;
  getExerciseGroups: (
    activityId: string,
    date: string,
    role: number
  ) => Promise<ExerciseGroupV2Model[]>;
  getActivityV1: (activityId: string, role: number) => Promise<ExerciseV1Model>;
  getAnalysis: (eventId: string, role: number) => Promise<WorkoutAnalysisType>;
};

const defaultFetchers: WorkoutPlanFetchers = {
  getWeeklyOverview: getWeeklyWorkoutOverview,
  getScheduledServices: getWeeklyScheduledServices,
  getExerciseGroups: getActivityExerciseGroups,
  getActivityV1: getActivityDetailsV1,
  getAnalysis: getWorkoutAnalysis,
};

export type WorkoutPlanContextValue = {
  weekStartDate: string;
  activeWeek: WorkoutWeek | undefined;
  activeWeekStatus: LoadStatus;
  /** The tab's gate; decided once by the first load of the current week. */
  planAvailability: PlanAvailability;
  /** Load state of the week that decides `planAvailability`. */
  probeStatus: LoadStatus;
  details: DetailsSelection | null;
  /** Fetched detail of the opened workout (undefined while loading / for events and services). */
  activeDetails: WorkoutDetails | undefined;
  activeDetailsStatus: LoadStatus;
  /**
   * Whether sessions may open their post-workout analysis: mobile gates it on
   * the member's advanced app access (the backend has no analysis otherwise).
   */
  analysisEnabled: boolean;
  /** The recorded session whose analysis is open over the details, if any. */
  analysis: AnalysisSelection | null;
  activeAnalysis: WorkoutAnalysisType | undefined;
  activeAnalysisStatus: LoadStatus;
  openAnalysis: (selection: AnalysisSelection) => void;
  closeAnalysis: () => void;
  retryAnalysis: () => void;
  goToWeek: (monday: string) => void;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToThisWeek: () => void;
  openDetails: (selection: DetailsSelection) => void;
  closeDetails: () => void;
  /** Refetch the week on screen, bypassing the cache (details are dropped too). */
  reload: () => void;
  /** Retry a failed details fetch for the open workout. */
  retryDetails: () => void;
  getDay: (date: string) => WorkoutDay | undefined;
  getActivity: (date: string, id: string) => Activity | undefined;
  getEvent: (date: string, id: string) => ActivityEvent | undefined;
  getService: (id: string) => ScheduledService | undefined;
};

export const WorkoutPlanContext = createContext<WorkoutPlanContextValue | null>(
  null
);

type Props = {
  subjectId: string;
  role: number;
  /** False keeps the provider mounted but idle (no requests), e.g. on other Programming tabs. */
  enabled?: boolean;
  /** The subject's `has_advanced_app_access`; false hides the analysis entry points. */
  analysisEnabled?: boolean;
  fetchers?: WorkoutPlanFetchers;
  /** Test seam: the date treated as "today". */
  today?: string;
  children: ReactNode;
};

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;

/**
 * Mount with `key={subjectId}` so switching client drops every cache.
 */
export const WorkoutPlanProvider = ({
  role,
  enabled = true,
  analysisEnabled = true,
  fetchers = defaultFetchers,
  today,
  children,
}: Props) => {
  const [state, dispatch] = useReducer(
    workoutPlanReducer,
    today ?? todayISO(),
    createInitialState
  );

  // One request per key at a time; components never fetch, so the NUT rate
  // limit is respected no matter how many of them read the same week.
  const weeksInFlight = useRef<Record<string, Promise<void>>>({});
  const detailsInFlight = useRef<Record<string, Promise<void>>>({});
  const analysisInFlight = useRef<Record<string, Promise<void>>>({});
  const fetchersRef = useRef(fetchers);
  fetchersRef.current = fetchers;
  const stateRef = useRef(state);
  stateRef.current = state;

  const ensureWeekly = useCallback(
    (monday: string, force = false): Promise<void> => {
      const status = stateRef.current.weeklyStatus[monday];
      if (!force && status && status !== "idle" && status !== "error") {
        return Promise.resolve();
      }
      const inFlight = weeksInFlight.current[monday];
      if (inFlight) return inFlight;

      dispatch({ type: "WEEKLY_LOADING", monday });
      const run = (async () => {
        try {
          // The services call is secondary: if it fails the week still shows
          // its workouts, without coached sessions.
          const [overview, services] = await Promise.all([
            fetchersRef.current.getWeeklyOverview(monday, role),
            fetchersRef.current
              .getScheduledServices(monday, role)
              .catch((error) => {
                console.error("[WorkoutPlan] scheduled services failed", monday, error);
                return [] as ScheduledService[];
              }),
          ]);
          const week: WorkoutWeek | null = overview
            ? { monday, daily: overview.daily ?? [], services: services ?? [] }
            : null;
          dispatch({ type: "WEEKLY_LOADED", monday, week });
        } catch (error) {
          if (isNotFound(error)) {
            dispatch({ type: "WEEKLY_LOADED", monday, week: null });
          } else {
            console.error("[WorkoutPlan] weekly overview failed", monday, error);
            dispatch({ type: "WEEKLY_FAILED", monday });
          }
        } finally {
          delete weeksInFlight.current[monday];
        }
      })();
      weeksInFlight.current[monday] = run;
      return run;
    },
    [role]
  );

  /**
   * Both detail calls, as the mobile screen makes them: the V2 exercise
   * groups and the legacy record (which carries the merged sessions for
   * every workout). V2 without exercises means a legacy workout. A 404 on
   * either call is "nothing there"; the detail is an error only when a call
   * failed for another reason and nothing came back.
   */
  const ensureDetails = useCallback(
    (id: string, date: string, force = false): Promise<void> => {
      const key = detailsKey(id, date);
      const status = stateRef.current.detailsStatus[key];
      if (!force && status && status !== "idle" && status !== "error") {
        return Promise.resolve();
      }
      const inFlight = detailsInFlight.current[key];
      if (inFlight) return inFlight;

      dispatch({ type: "DETAILS_LOADING", key });
      const run = (async () => {
        try {
          const [groupsResult, recordResult] = await Promise.allSettled([
            fetchersRef.current.getExerciseGroups(id, date, role),
            fetchersRef.current.getActivityV1(id, role),
          ]);
          const groups =
            groupsResult.status === "fulfilled" ? groupsResult.value ?? [] : [];
          const record =
            recordResult.status === "fulfilled" ? recordResult.value ?? null : null;
          const failed = [groupsResult, recordResult].filter(
            (result) => result.status === "rejected" && !isNotFound(result.reason)
          );
          failed.forEach((result) =>
            console.error(
              "[WorkoutPlan] workout detail failed",
              id,
              (result as PromiseRejectedResult).reason
            )
          );
          if (isV2Detail(groups)) {
            dispatch({ type: "DETAILS_LOADED", key, details: { version: "v2", groups, record } });
          } else if (record) {
            dispatch({ type: "DETAILS_LOADED", key, details: { version: "v1", groups: [], record } });
          } else if (failed.length > 0) {
            dispatch({ type: "DETAILS_FAILED", key });
          } else {
            dispatch({ type: "DETAILS_LOADED", key, details: null });
          }
        } finally {
          delete detailsInFlight.current[key];
        }
      })();
      detailsInFlight.current[key] = run;
      return run;
    },
    [role]
  );

  /** One analysis fetch per session; a 404 is "no analysis for this session". */
  const ensureAnalysis = useCallback(
    (eventId: string, force = false): Promise<void> => {
      const status = stateRef.current.analysisStatus[eventId];
      if (!force && status && status !== "idle" && status !== "error") {
        return Promise.resolve();
      }
      const inFlight = analysisInFlight.current[eventId];
      if (inFlight) return inFlight;

      dispatch({ type: "ANALYSIS_LOADING", eventId });
      const run = (async () => {
        try {
          const analysis = await fetchersRef.current.getAnalysis(eventId, role);
          dispatch({ type: "ANALYSIS_LOADED", eventId, analysis: analysis ?? null });
        } catch (error) {
          if (isNotFound(error)) {
            dispatch({ type: "ANALYSIS_LOADED", eventId, analysis: null });
          } else {
            console.error("[WorkoutPlan] workout analysis failed", eventId, error);
            dispatch({ type: "ANALYSIS_FAILED", eventId });
          }
        } finally {
          delete analysisInFlight.current[eventId];
        }
      })();
      analysisInFlight.current[eventId] = run;
      return run;
    },
    [role]
  );

  // Fetch the week on screen. Cached weeks return immediately.
  useEffect(() => {
    if (enabled) ensureWeekly(state.weekStartDate);
  }, [enabled, state.weekStartDate, ensureWeekly]);

  // Fetch the opened session's analysis.
  useEffect(() => {
    if (enabled && state.analysis) ensureAnalysis(state.analysis.eventId);
  }, [enabled, state.analysis, ensureAnalysis]);

  // Fetch the opened workout's detail; events and services need none.
  useEffect(() => {
    if (enabled && state.details?.kind === "workout") {
      ensureDetails(state.details.id, state.details.date);
    }
  }, [enabled, state.details, ensureDetails]);

  const reload = useCallback(() => {
    dispatch({ type: "DETAILS_CLEARED" });
    ensureWeekly(stateRef.current.weekStartDate, true);
    // The open drawer keeps its selection, so refetch its detail as well.
    const selection = stateRef.current.details;
    if (selection?.kind === "workout") {
      ensureDetails(selection.id, selection.date, true);
    }
  }, [ensureWeekly, ensureDetails]);

  const retryAnalysis = useCallback(() => {
    const selection = stateRef.current.analysis;
    if (selection) ensureAnalysis(selection.eventId, true);
  }, [ensureAnalysis]);

  const retryDetails = useCallback(() => {
    const selection = stateRef.current.details;
    if (selection?.kind === "workout") {
      ensureDetails(selection.id, selection.date, true);
    }
  }, [ensureDetails]);

  const activeWeek = state.weeklyCache[state.weekStartDate];
  const activeWeekStatus = state.weeklyStatus[state.weekStartDate] ?? "idle";
  const probeStatus = state.weeklyStatus[state.probeMonday] ?? "idle";
  const activeKey =
    state.details?.kind === "workout"
      ? detailsKey(state.details.id, state.details.date)
      : null;
  const activeDetails = activeKey ? state.detailsCache[activeKey] : undefined;
  const activeDetailsStatus = activeKey
    ? state.detailsStatus[activeKey] ?? "idle"
    : "idle";
  const activeAnalysis = state.analysis
    ? state.analysisCache[state.analysis.eventId]
    : undefined;
  const activeAnalysisStatus = state.analysis
    ? state.analysisStatus[state.analysis.eventId] ?? "idle"
    : "idle";

  const value = useMemo<WorkoutPlanContextValue>(
    () => ({
      weekStartDate: state.weekStartDate,
      activeWeek,
      activeWeekStatus,
      planAvailability: state.planAvailability,
      probeStatus,
      details: state.details,
      activeDetails,
      activeDetailsStatus,
      analysisEnabled,
      analysis: state.analysis,
      activeAnalysis,
      activeAnalysisStatus,
      openAnalysis: (selection) => dispatch({ type: "OPEN_ANALYSIS", selection }),
      closeAnalysis: () => dispatch({ type: "CLOSE_ANALYSIS" }),
      retryAnalysis,
      goToWeek: (monday) => dispatch({ type: "GO_TO_WEEK", monday }),
      goToPreviousWeek: () =>
        dispatch({
          type: "GO_TO_WEEK",
          monday: addDaysISO(stateRef.current.weekStartDate, -7),
        }),
      goToNextWeek: () =>
        dispatch({
          type: "GO_TO_WEEK",
          monday: addDaysISO(stateRef.current.weekStartDate, 7),
        }),
      goToThisWeek: () =>
        dispatch({ type: "GO_TO_WEEK", monday: thisWeekMonday() }),
      openDetails: (selection) => dispatch({ type: "OPEN_DETAILS", selection }),
      closeDetails: () => dispatch({ type: "CLOSE_DETAILS" }),
      reload,
      retryDetails,
      getDay: (date) => selectDay(stateRef.current, date),
      getActivity: (date, id) =>
        findActivity(stateRef.current.weeklyCache[state.weekStartDate], date, id),
      getEvent: (date, id) =>
        findEvent(stateRef.current.weeklyCache[state.weekStartDate], date, id),
      getService: (id) =>
        findService(stateRef.current.weeklyCache[state.weekStartDate], id),
    }),
    [
      state,
      activeWeek,
      activeWeekStatus,
      probeStatus,
      activeDetails,
      activeDetailsStatus,
      activeAnalysis,
      activeAnalysisStatus,
      analysisEnabled,
      reload,
      retryDetails,
      retryAnalysis,
    ]
  );

  return (
    <WorkoutPlanContext.Provider value={value}>
      {children}
    </WorkoutPlanContext.Provider>
  );
};

export default WorkoutPlanProvider;
